/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState, useRef, useEffect } from "react";

interface CounterControlProps {
  initialValue?: number;
  min?: number;
  max?: number;
  onChange?: (value: number) => void;
  buttonColor?: string;
  buttonTextColor?: string;
  valueColor?: string;
  size?: number;
  gap?: number;
  labelText?: string;
  longPressDelay?: number;
  longPressInterval?: number;
}

const CounterControl = ({
  initialValue = 10,
  min = 0,
  max = 20,
  onChange = () => {},
  buttonColor = "#00A9CE",
  buttonTextColor = "#FFFFFF",
  valueColor = "#000000",
  size = 50,
  gap = 20,
  labelText = "",
  longPressDelay = 500, // Initial delay before rapid changes start
  longPressInterval = 150, // Interval between changes during long press
}: CounterControlProps) => {
  const [value, setValue] = useState(initialValue);
  const incrementTimerRef = useRef<number | null>(null);
  const decrementTimerRef = useRef<number | null>(null);
  const longPressTimerRef = useRef<number | null>(null);

  // Clean up any timers when component unmounts
  useEffect(() => {
    return () => {
      if (incrementTimerRef.current)
        window.clearInterval(incrementTimerRef.current);
      if (decrementTimerRef.current)
        window.clearInterval(decrementTimerRef.current);
      if (longPressTimerRef.current)
        window.clearTimeout(longPressTimerRef.current);
    };
  }, []);

  // Handle value changes
  const updateValue = (newValue: number) => {
    // Clamp value between min and max
    const clampedValue = Math.max(min, Math.min(max, newValue));
    setValue(clampedValue);
    onChange(clampedValue);
    return clampedValue;
  };

  // Increment functions
  const increment = () => {
    if (value < max) {
      const newValue = value + 1;
      return updateValue(newValue);
    }
    return value;
  };

  const startIncrement = () => {
    // Initial increment
    const newValue = increment();

    // Setup long press behavior
    longPressTimerRef.current = window.setTimeout(() => {
      // Continue incrementing at interval if still below max
      if (newValue < max) {
        incrementTimerRef.current = window.setInterval(() => {
          setValue((prevValue) => {
            if (prevValue >= max) {
              // Stop if we hit max
              if (incrementTimerRef.current) {
                window.clearInterval(incrementTimerRef.current);
                incrementTimerRef.current = null;
              }
              return max;
            }
            const nextValue = prevValue + 1;
            onChange(nextValue);
            return nextValue;
          });
        }, longPressInterval);
      }
    }, longPressDelay);
  };

  const stopIncrement = () => {
    if (incrementTimerRef.current) {
      window.clearInterval(incrementTimerRef.current);
      incrementTimerRef.current = null;
    }
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Decrement functions
  const decrement = () => {
    if (value > min) {
      const newValue = value - 1;
      return updateValue(newValue);
    }
    return value;
  };

  const startDecrement = () => {
    // Initial decrement
    const newValue = decrement();

    // Setup long press behavior
    longPressTimerRef.current = window.setTimeout(() => {
      // Continue decrementing at interval if still above min
      if (newValue > min) {
        decrementTimerRef.current = window.setInterval(() => {
          setValue((prevValue) => {
            if (prevValue <= min) {
              // Stop if we hit min
              if (decrementTimerRef.current) {
                window.clearInterval(decrementTimerRef.current);
                decrementTimerRef.current = null;
              }
              return min;
            }
            const nextValue = prevValue - 1;
            onChange(nextValue);
            return nextValue;
          });
        }, longPressInterval);
      }
    }, longPressDelay);
  };

  const stopDecrement = () => {
    if (decrementTimerRef.current) {
      window.clearInterval(decrementTimerRef.current);
      decrementTimerRef.current = null;
    }
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  return (
    <div className="flex flex-col items-center w-full m-2">
      {labelText && (
        <label className="mb-2 text-lg font-medium">{labelText}</label>
      )}

      <div
        className="flex items-center justify-between max-w-md"
        style={{ gap: `${gap}px` }}
      >
        <button
          // onClick={decrement}
          // onMouseDown={startDecrement}
          // onMouseUp={stopDecrement}
          // onMouseLeave={stopDecrement}
          onTouchStart={startDecrement}
          onTouchEnd={stopDecrement}
          className="flex items-center justify-center rounded-full focus:outline-none"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: "transparent",
            border: `2px solid ${buttonColor}`,
            cursor: value <= min ? "not-allowed" : "pointer",
            opacity: value <= min ? 0.5 : 1,
          }}
          disabled={value <= min}
          aria-label="Decrease value"
        >
          <span
            style={{
              color: buttonColor,
              fontSize: `${size / 2}px`,
              fontWeight: "bold",
            }}
          >
            -
          </span>
        </button>

        <span
          style={{
            color: valueColor,
            fontSize: "32px",
            fontFamily: "Figtree",
            fontWeight: 700,
            lineHeight: "41px",
            textAlign: "center",
            letterSpacing: "0.374px",
            height: "41px",
            margin: `0 ${gap / 4}px`,
            minWidth: "48px", // Ensures value display has consistent width
          }}
        >
          {value}
        </span>

        <button
          // onClick={increment}
          // onMouseDown={startIncrement}
          // onMouseUp={stopIncrement}
          // onMouseLeave={stopIncrement}
          onTouchStart={startIncrement}
          onTouchEnd={stopIncrement}
          className="flex items-center justify-center rounded-full focus:outline-none"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: "transparent",
            border: `2px solid ${buttonColor}`,
            cursor: value >= max ? "not-allowed" : "pointer",
            opacity: value >= max ? 0.5 : 1,
          }}
          disabled={value >= max}
          aria-label="Increase value"
        >
          <span
            style={{
              color: buttonColor,
              fontSize: `${size / 2}px`,
              fontWeight: "bold",
            }}
          >
            +
          </span>
        </button>
      </div>
    </div>
  );
};

export default CounterControl;
